import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';

import { VendorManagementQuery } from 'src/app/vendor-management/vendor-managment.query';
import { FrameworksService } from 'src/app/data-types/services/frameworks.service';
import { IntegrationSystem } from 'src/app/api/models/systems/systems.interface';
import { DisplayNumberPipe } from 'src/app/shared/pipes/display-number.pipe';
import { ContentPipe } from 'src/app/services/content/content.pipe';
import { SystemsColumnKeys } from '../../models/systems-column.enum';
import { SystemsQuery } from '../../state/systems.query';
import { SystemInstance } from '../../models/systems.interface';
import { ReviewStatus, SuggestedStateEnum } from 'src/app/api/models/systems/systems.enum';
import { DataTypesService } from 'src/app/data-types/services/data-types.service';
import { FeedbackStateEnum } from 'src/app/api/models/feedback/feedback.enum';
import { ImpactAssessmentService } from 'src/app/impact-assessment/services/impact-assessment.service';
import { CustomFieldsV2Service } from 'src/app/company-settings/custom-fields-v2/custom-fields-v2.service';
import { CustomFieldInputTypeEnum, PredefinedFieldNameEnum } from 'src/app/api/models/company-settings/custom-field-v2.enum';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';
import { SystemFieldIdToNamePipe } from "../../pipes/system-field-id-to-name.pipe";
import { SpecialSystemType } from '../../models/custom-system-type.enum';
import { RiskCalulationPipe } from 'src/app/ai-assessments/pipes/risk-calulation.pipe';
import { RiskCalulationToTextPipe } from 'src/app/ai-assessments/pipes/risk-calculation-to-text.pipe';
import { AssessmentsService } from 'src/app/ai-assessments/services/assessments.service';
import { SystemIntegrationToTextPipe } from '../../pipes/system-integration-to-text.pipe';
import { SystemUsageLabelPipe } from '../../pipes/system-usage-label.pipe';
import { DISCOVERY_DATE_FORMAT } from './systems-table.constants';

@Injectable()
export class SystemsTableCsvHelper {
  readonly mineCustomSystemType = SpecialSystemType.Custom;

  constructor(
      private contentPipe: ContentPipe,
      private displayNumberPipe: DisplayNumberPipe,
      private riskCalulation: RiskCalulationPipe,
      private riskCalulationToTextPipe: RiskCalulationToTextPipe,
      private frameworksService: FrameworksService,
      private dataTypesService: DataTypesService,
      private systemsQuery: SystemsQuery,
      private vendorManagementQuery: VendorManagementQuery,
      private impactAssessmentService: ImpactAssessmentService,
      private customFieldsV2Service: CustomFieldsV2Service,
      private featureFlagQuery: FeatureFlagQuery,
      private systemFieldIdToNamePipe: SystemFieldIdToNamePipe,
      private assessmentsService: AssessmentsService,
      private systemIntegrationToText: SystemIntegrationToTextPipe,
      private datePipe: DatePipe,
      private systemUsageLabelPipe: SystemUsageLabelPipe,
  ) {}

  getValueByKey(instance: IntegrationSystem, key: string): string {
    const system = this.systemsQuery.getEntity(instance.systemId);
    const values: Record<SystemsColumnKeys, string> = {
      businessOwner: system.owner ?? '-',
      stakeHolders: system.stakeHolders?.join(', ') ?? '-',
      category: system.category ?? '-',
      counter: system.connectedAccounts ? `${this.displayNumberPipe.transform(system.connectedAccounts)}` : '-',
      dataSourceOrigin: system.dataSourceOrigin.toString() ?? '-',
      dataTypes: this.getDataTypes(system),
      feedback: this.getFeedback(system),
      frameworks: this.getFrameworks(system),
      instanceType: this.getInstanceType(instance),
      locations: system.serverLocations?.join(', ') ?? '-',
      review: system?.reviewStatus?.status ?? '-',
      ropa: this.getRopa(system),
      system: this.systemIntegrationToText.transform(system.name, instance.customName),
      cyberRating: this.getRating(system.systemId),
      riskCalc: this.riskCalulationToTextPipe.transform(this.riskCalulation.transform(system.vendorRisk?.severity, system.vendorRisk?.likelihood)) ?? '-',
      mitigations: (system.vendorRisk?.mitigations.length > 0).toString() ?? '-',
      highPotentialRisk: this.getHightPotentialRisk(system),
      functions: this.getFunctions(system),
      reviewDate: system.reviewStatus?.status === ReviewStatus.Approved ? this.datePipe.transform(system.reviewStatus?.lastActionDate, this.getDateFormat(system.reviewStatus?.lastActionDate)) : '-',
      usage: this.systemUsageLabelPipe.transform(system) || '-',
      discoveryDate: system.discoveryDate ? this.datePipe.transform(system.discoveryDate, DISCOVERY_DATE_FORMAT) : '-',
    };

    if (!!values[key]) {
      return values[key];
    }

    const customFieldKey = system.customFields?.find(field => field.id === key);
    
    if (!!customFieldKey) {
      const field = this.customFieldsV2Service.getField(customFieldKey.id);
      if (field?.inputType === CustomFieldInputTypeEnum.Select) {
        const validOptions = customFieldKey.values.optionIds
          .map(id => field.options?.find(o => o.id === id)?.text)
          .filter(text => text);
        return validOptions.length > 0 ? validOptions.join(', ') : '-';
      }
      return customFieldKey.values.text ?? '-';
    }

    return customFieldKey?.value ?? '-';
  }

  private getFunctions(system: SystemInstance): string {
    let functions = system.functions ?? [];
    // New v2 saves carry option UUIDs; legacy data may still carry predefined option text — accept both.
    const buOptions = this.customFieldsV2Service.getField(PredefinedFieldNameEnum.BusinessUnit)?.options ?? [];
    const liveIds = new Set<string>();
    buOptions.forEach(o => {
      liveIds.add(o.id);
      if (o.isPredefined) liveIds.add(o.text);
    });
    functions = functions.filter(f => liveIds.has(f?.id?.trim()));
    return functions.length ? functions.map(f => this.systemFieldIdToNamePipe.transform(f.id)).join(', ') : '-';
  }

  private getFrameworks(system: SystemInstance): string {
    const extendedDataTypes = system.extendedDataTypes.filter(dataType => dataType.state === SuggestedStateEnum.Accepted);
    const combinedDataTypes = this.dataTypesService.getExtendedDataTypes(extendedDataTypes);  
    return this.frameworksService.getFrameworksChips(combinedDataTypes).map(type => type.text)?.join(', ') ?? '-';
  }

  private getDataTypes(system: SystemInstance): string {
    const extendedDataTypes = system.extendedDataTypes.filter(dataType => dataType.state === SuggestedStateEnum.Accepted);
    return `${this.dataTypesService.getExtendedDataTypes(extendedDataTypes)?.length}`;
  }

  private getInstanceType(instance: IntegrationSystem): string {
    return instance.enabled ? instance.instanceType : '-';
  }

  private getRating(id: string): string {
    const risk = this.vendorManagementQuery.getEntity(id);
    return risk?.riskRating ?? '-';
  }
  
  private getHightPotentialRisk(system: SystemInstance): string {
    return this.impactAssessmentService.getHighRiskNum(system).toString() ?? '-';
  }

  private getFeedback(system: SystemInstance): string {
    switch (system.generalFeedbackState) {
      case FeedbackStateEnum.Pending:
        return this.contentPipe.transform('common.pendingBadgeText');

      case FeedbackStateEnum.Responded:
        return this.contentPipe.transform('common.respondedBadgeText');

      default:
        return '-';
    }
  }
  
  private getRopa(system: SystemInstance): string {
    // if (this.featureFlagQuery.getFlag(FeatureFlags.DevAssessmentBackendPaging)) {
    //   const activities = this.assessmentsService.getActivitiesBySystemId(system.systemId, true);
    //   return `${activities().pages.total}`;
    // }
    // else {
      return `${this.assessmentsService.getActivitiesBySystemId(system.systemId)().length}`;
    //}
  }

  getDateFormat(date: string): string {
		return new Date().getFullYear() === new Date(date).getFullYear() ? 'dd MMMM' : 'dd MMMM yyyy';
	}
}
